import { motion } from "framer-motion";
import { ASSETS } from "@/lib/brand";
import { PartnerCarousel } from "@/components/site/PartnerCarousel";

const fadeIn = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.95, ease: "easeOut" },
};

export function Artisanship() {
  return (
    <section
      id="artigianalita"
      data-testid="artisanship-section"
      className="relative bg-[#0A0A0A] text-[#F2EFE8] overflow-hidden scroll-mt-20 sm:scroll-mt-24"
    >
      <div className="absolute inset-0 grain opacity-30" aria-hidden />

      <div className="relative max-w-[1480px] mx-auto px-6 sm:px-12 lg:px-20 py-24 sm:py-32 lg:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <motion.div {...fadeIn} className="lg:col-span-7 order-2 lg:order-1 relative">
            <div
              className="zoom-frame relative rounded-[32px] overflow-hidden"
              style={{ boxShadow: "0 0 0 1px rgba(196,166,113,0.5), 0 0 52px rgba(196,166,113,0.3), 0 24px 60px rgba(0,0,0,0.45)" }}
            >
              <img
                src={ASSETS.artisan}
                alt="Mano che annoda il cartellino in carta Oro Lucido numerato a mano con sigillo QR Code accanto alla bottiglia Blanche Limited Edition"
                className="w-full h-[420px] sm:h-[640px] lg:h-[780px] object-cover"
                data-testid="artisanship-image"
              />
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{ boxShadow: "inset 0 0 100px rgba(0,0,0,0.4)", background: "radial-gradient(ellipse at center, transparent 58%, rgba(0,0,0,0.28) 100%)" }}
              />
            </div>
          </motion.div>

          {/* Text */}
          <motion.div
            {...fadeIn}
            transition={{ ...fadeIn.transition, delay: 0.15 }}
            className="lg:col-span-5 order-1 lg:order-2"
          >
            <div className="relative z-10 flex items-center gap-6 mb-6 pt-2">
              <span className="section-num text-3xl" style={{ color: "#C4A671" }}>
                08
              </span>
              <div className="h-px w-12 bg-[#C4A671]" />
              <span className="overline text-[10px]" style={{ color: "#C4A671" }}>
                Artigianalità
              </span>
            </div>

            <h2
              className="font-serif-display leading-[0.95] text-[#F2EFE8]"
              style={{ fontSize: "clamp(2.6rem, 6vw, 5rem)" }}
            >
              Il dettaglio
              <br />
              <span className="italic font-light text-[#C4A671]">
                che fa la firma.
              </span>
            </h2>

            <p className="mt-8 text-[16px] leading-[1.9] text-[#C9C3B5] font-light max-w-md">
              Ogni bottiglia esce dalla cantina con il proprio cartellino
              descrittivo, annodato a mano. Un gesto deliberato, quasi rituale,
              che racconta la nostra ossessione per il dettaglio e la cura
              artigianale con cui curiamo ogni singola allocazione.
            </p>

            <p className="mt-10 overline text-[10px]" style={{ color: "#C4A671" }}>
              I Nostri Collarini
            </p>

            <div className="mt-4 space-y-5">
              <Detail
                label="Linea Territorio & Limited Edition (Scilla, Claire & Terra Arsa)"
                value="Carta materica testurizzata coordinata all'etichetta: nero opaco per Terra Arsa, bianco opaco per Scilla e per la Limited Edition Claire (impreziosita da dettagli in Foil Rosa Gold). Sul retro, il QR Code sblocca il Concierge Digitale con le note del Vigneron."
              />
              <Detail
                label="Limited Edition (Blanche)"
                value="Il cartellino d'élite in preziosa carta Oro Lucido, numerato progressivamente a mano da 1 a 1.333. Il QR Code dedicato apre le porte del Sommelier Club: una guida sartoriale ed emozionale per celebrare la rarità di questo Blanc de Noirs unico al mondo."
              />
              <Detail
                label="Etichette"
                value="Sartoriali e testurizzate, con stemma araldico in rilievo lavorato a sbalzo. Nero opaco profondo per il rosso Terra Arsa; bianco opaco luminoso per il bianco Scilla, l'esclusiva Limited Edition Blanche e il rosato Claire (firmato dal nome in Foil Rosa Gold)."
              />
              <Detail
                label="Sigillo"
                value="Capsula coordinata (nera per Terra Arsa, bianca per Scilla, Blanche e Claire), elegante finitura matte impreziosita dalla corona del nostro logo araldico impressa in oro."
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Detail({ label, value }) {
  return (
    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-12 sm:gap-4 py-3 border-b border-[#1f1f1f]">
      <div className="sm:col-span-4">
        <span className="overline text-[10px] sm:text-[9.5px] text-[#C4A671]">{label}</span>
      </div>
      <div className="sm:col-span-8">
        <p className="text-[15.5px] leading-[1.75] text-[#C9C3B5] font-light">
          {value}
        </p>
      </div>
    </div>
  );
}

export function SommelierClub() {
  return (
    <section
      id="sommelier-club"
      data-testid="sommelier-section"
      className="relative bg-white overflow-x-clip"
    >
      <div className="max-w-[1480px] mx-auto px-6 sm:px-12 lg:px-20 py-24 sm:py-32 lg:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Text (left) */}
          <motion.div {...fadeIn} className="lg:col-span-5 order-1">
            <div className="flex items-center gap-6 mb-6">
              <span className="section-num text-3xl">09</span>
              <div className="h-px w-12 bg-[#0A0A0A]" />
              <span className="overline text-[10px]">Sommelier Club</span>
            </div>

            <h2
              className="font-serif-display leading-[0.95] text-[#0A0A0A]"
              style={{ fontSize: "clamp(2.6rem, 6vw, 5rem)" }}
            >
              Il nostro
              <br />
              <span className="italic font-light text-[#63171B]">
                gesto rituale.
              </span>
            </h2>

            <p className="mt-8 text-[16px] leading-[1.9] text-[#2C2C2C] font-light max-w-md">
              I nostri vini vivono nelle mani dei migliori sommelier d'Italia
              ed Europa. Il{" "}
              <em className="font-serif-display text-[#0A0A0A]">
                Sommelier Club
              </em>{" "}
              è la rete esclusiva di partner Ho.Re.Ca. selezionati per
              raccontare e servire le nostre referenze ai loro ospiti più
              esigenti.
            </p>

            <div className="mt-10 grid grid-cols-2 gap-8 max-w-sm">
              <StatBlock n="50+" label="Ristoranti Premium" />
              <StatBlock n="12" label="Luxury Hotel" />
              <StatBlock n="08" label="Wine Bar Curati" />
              <StatBlock n="06" label="Mercati Export" />
            </div>

            <button
              onClick={() =>
                document
                  .getElementById("contatti")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="btn-ghost mt-12"
              data-testid="sommelier-cta"
            >
              Diventa Partner <span aria-hidden>→</span>
            </button>
          </motion.div>

          {/* Image (right) */}
          <motion.div
            {...fadeIn}
            transition={{ ...fadeIn.transition, delay: 0.15 }}
            className="lg:col-span-7 order-2 relative"
          >
            <div
              className="zoom-frame relative rounded-[32px] overflow-hidden"
              style={{ boxShadow: "0 0 0 1px rgba(74,127,155,0.4), 0 0 46px rgba(74,127,155,0.24), 0 24px 60px rgba(10,10,10,0.16)" }}
            >
              <img
                src={ASSETS.sommelier}
                alt="Sommelier del Sommelier Club in completo scuro che versa il bianco Scilla nel calice, con secchiello nero satinato e stemma araldico in oro"
                className="w-full h-[420px] sm:h-[640px] lg:h-[780px] object-cover"
                data-testid="sommelier-image"
              />
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{ boxShadow: "inset 0 0 90px rgba(10,10,10,0.2)", background: "radial-gradient(ellipse at center, transparent 60%, rgba(10,10,10,0.12) 100%)" }}
              />
            </div>
          </motion.div>
        </div>

        {/* Carosello vantaggi Partner d'Allocazione */}
        <PartnerCarousel />
      </div>
    </section>
  );
}

function StatBlock({ n, label }) {
  return (
    <div>
      <div className="font-serif-display text-4xl text-[#0A0A0A] leading-none">
        {n}
      </div>
      <div className="overline text-[9px] mt-2">{label}</div>
    </div>
  );
}
